<template>
  <div class="ProductCard__SwatchSlider js-swatch-variant" v-if="hasSwatches">
    <button v-if="hasButtons" role="button" data-direction="prev" @click="move('-')" class="ProductCard__SwatchBtn" :class="{ 'ProductCard__SwatchBtn--Disabled': atStart }">
      <Icon icon="bookend-arrow" />
    </button>
    <Splide :options="config" ref="swatchSlider" @splide:resized="determinePosition" @splide:moved="determinePosition" @splide:mounted="determinePosition">
      <SplideSlide v-for="swatch in swatches" :key="swatch" :class="swatchClasses(swatch)">
        <button role="button" :aria-label="`${swatch} swatch button`" @click="selection(swatch)">
          <div role="presentation" v-if="isHex(swatch)" :style="fetchSwatch(swatch)" data-ht-element="swatch" :data-ht-id="swatch"></div>
          <img v-else :src="fetchSwatch(swatch)" data-ht-element="swatch" :data-ht-id="swatch" :alt="swatch" />
        </button>
      </SplideSlide>
    </Splide>
    <button v-if="hasButtons" role="button" data-direction="next" @click="move('+')" class="ProductCard__SwatchBtn" :class="{ 'ProductCard__SwatchBtn--Disabled': atEnd }">
      <Icon icon="bookend-arrow" />
    </button>
  </div>

  <ul class="ProductCard__Swatches js-swatch-control" data-ht-element="swatches" v-if="hasSwatches" ref="list">
    <li v-for="swatch in swatches.slice(0, limit)" :key="swatch" v-show="displaySwatch(swatch)" :class="swatchClasses(swatch)">
      <button role="button" :aria-label="`${swatch} swatch button`" @click="selection(swatch)">
        <div role="presentation" v-if="isHex(swatch)" :style="fetchSwatch(swatch)" data-ht-element="swatch" :data-ht-id="swatch"></div>
        <img v-else :src="fetchSwatch(swatch)" data-ht-element="swatch" :data-ht-id="swatch" :alt="swatch" />
      </button>
    </li>
    <li v-if="remainder" class="ProductCard__Swatch p6 ProductCard__Swatch--Remainder">
      <a :href="url">+{{ remainder }} more</a>
    </li>
  </ul>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { fetchSwatch, isHex } from './Composables/ProductCard.js';
import { SplideSlide, Splide } from '@splidejs/vue-splide';
import { pubSub } from '@utils/PubSub.js';
import Icon from '../Global/Icon.vue';

const list = ref(null);
const swatchSlider = ref(null);
const atStart = ref(false);
const atEnd = ref(false);
const active = ref(0);
const limit = ref(3);
const selected = ref(false);
const remainder = ref(false);
const hasSwatches = ref(false);

const emits = defineEmits(['updateCard']);
const props = defineProps({
  allOptions: Array,
  selectedOptions: Array,
  swatches: Array,
  url: String,
  selectionListener: Boolean,
  secondaryItems: {
    type: Array,
    default: [],
  },
  hiddenOptions: {
    type: Array,
    default: [],
  },
});

const config = {
  type: 'slide',
  gap: 6,
  arrows: false,
  pagination: false,
  height: 26,
  width: 'calc(100% - 48px)', //Accounts for button widths
  fixedWidth: 26,
  fixedHeight: 26,
};

const move = (direction) => {
  const slider = swatchSlider.value.splide.Components.Controller;
  const index = slider.getIndex();

  active.value = direction === '+' ? index + 4 : index - 4;

  return slider.go(active.value);
};

const determinePosition = (splide) => {
  setTimeout(() => {
    const end = splide.Components.Controller.getEnd();
    const slides = splide.Components.Elements.slides;

    const firstSlide = slides[0].classList;
    const lastSlide = slides[end].classList;

    atStart.value = firstSlide.contains('is-visible');
    atEnd.value = lastSlide.contains('is-visible');
  }, 0);
};

const hasButtons = computed(() => {
  const startVisible = atStart.value;
  const endVisible = atEnd.value;
  const bookendsVisibile = startVisible && endVisible;

  return bookendsVisibile === false;
});

const getCurrentSwatchLimit = () => {
  /**
   * @returns {number} The displayed number of swatches based on container width:
   * - 3 if width is 272px or less
   * - 7 if width is greater than 273px
   */
  const containerWidth = list.value?.parentElement.clientWidth;

  if (containerWidth <= 272) {
    return 3;
  } else {
    return 7;
  }
};

const swatches = computed(() => {
  const values = props?.swatches ?? [];
  const secondaryItems = props?.secondaryItems ?? [];
  if (secondaryItems.length <= 0) return values;

  const secondaryValues = secondaryItems.flatMap((item) => item.swatches.values);

  return [...new Set(values), ...new Set(secondaryValues)];
});

const updateLimit = () => {
  const hiddenOptions = props?.hiddenOptions ?? [];
  const hiddenColors = [...new Set(swatches.value.filter((swatch) => hiddenOptions.includes(swatch)))];
  const totalHiddenColors = hiddenColors.length;

  limit.value = getCurrentSwatchLimit();
  remainder.value = swatches.value.length - limit.value - totalHiddenColors;
  if (remainder.value <= 0) remainder.value = false;
};

const selection = (context = false) => {
  const options = props?.selectedOptions || false;
  if (!options) return;

  const color = options.filter((option) => option.name === 'Color')[0] || [];

  if (color.length <= 0) return;

  selected.value = context || color.value;

  const selectedSwatch = {
    name: color.name,
    value: selected.value,
  };
  emits('updateCard', selectedSwatch);
};

const listenForPDPSelections = () => {
  pubSub.subscribe('pdp:selection:update', ({ detail }) => {
    const { value } = detail;
    const flatOptions = props.allOptions.flatMap((option) => option.values);
    const hasMatchingValue = flatOptions.includes(value);
    if (hasMatchingValue) selection(value);
  });
};

const swatchClasses = (swatch) => {
  const isActive = selected.value === swatch;
  const inactive = `ProductCard__Swatch`;
  const active = `ProductCard__Swatch ProductCard__Swatch--Active`;
  return isActive ? active : inactive;
};

const displaySwatch = (swatch) => {
  const hiddenOptions = props?.hiddenOptions ?? [];
  const isVisible = hiddenOptions.filter((option) => option === swatch).length === 0;

  if (isVisible) return fetchSwatch(swatch);
  else return false;
};

onMounted(() => {
  hasSwatches.value = swatches.value.length > 0;
  if (!hasSwatches.value) return;

  selected.value = props.selectedOptions;

  if (props.selectionListener) {
    listenForPDPSelections();
  }
  selection();
  window.addEventListener('resize', () =>
    setTimeout(() => {
      updateLimit();
    }, 0)
  );
});

watch(list, updateLimit, { flush: 'post' });

watch(props.selectedOptions, () => {
  const color = props.selectedOptions.filter((option) => option.name === 'Color')[0] || [];
  if (color.length <= 0) return;
  selected.value = color.value;
});
</script>
